import React from 'react';
import { Badge, Modal, Table } from 'react-bootstrap';
import { convertHoursToHM } from '../../utils';

/**
 * DatSummaryModal — hiển thị tổng kết kết quả DAT của 1 học viên trong
 * modal, dùng chung cho AdminPageV2 và SearchPageV2.
 *
 * `summary` là envelope BE trả từ GET /v2/dat/student/summary:
 *   { registrationCode, license, totals: {...}, details: {...},
 *     valid, allChecked, sessionCount, message }
 * FE chỉ hiển thị — mọi con số & kết luận do BE tính.
 *
 * Props:
 *   show       : boolean
 *   onHide     : () => void
 *   summary    : object | null
 *   studentName: string (tuỳ chọn, hiển thị tên học viên)
 */
export default function DatSummaryModal({ show, onHide, summary, studentName }) {
  const totals = summary?.totals || {};
  const details = summary?.details || {};
  const km = (v) => `${Math.floor((v || 0) * 100) / 100} km`;

  // 1 dòng chỉ tiêu: nhãn · giá trị · trạng thái (Đã đủ / Thiếu…).
  // `highlight` = true → tô đỏ đậm giá trị (dùng cho QĐ tự động > 120km).
  const Row = ({ label, value, status, highlight }) => (
    <tr>
      <td>{label}</td>
      <td className={highlight ? 'fw-bold text-danger' : 'fw-bold'}>{value}</td>
      <td>
        {status != null && (
          <span className={status === 'Đã đủ' ? 'text-success' : 'text-danger'}>
            {status}
          </span>
        )}
      </td>
    </tr>
  );

  const isPass = Boolean(summary?.valid && summary?.allChecked);

  return (
    <Modal show={show} onHide={onHide} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Tổng kết kết quả DAT</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!summary ? (
          <p className='text-muted mb-0'>Không có dữ liệu tổng kết.</p>
        ) : (
          <>
            <p className='mb-2'>
              {studentName && (
                <>
                  Học viên: <strong>{studentName}</strong>{' '}
                </>
              )}
              <span className='text-muted'>
                ({summary.registrationCode}
                {summary.license ? ` · hạng ${summary.license}` : ''}
                {summary.sessionCount != null ? ` · ${summary.sessionCount} phiên` : ''})
              </span>
            </p>

            {summary.message ? (
              <p className='text-danger'>{summary.message}</p>
            ) : (
              <Table bordered size='sm' className='mb-3'>
                <thead>
                  <tr>
                    <th>Chỉ tiêu</th>
                    <th>Giá trị</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  <Row
                    label='Tổng thời gian'
                    value={convertHoursToHM(totals.totalDuration)}
                    status={details.totalTG}
                  />
                  <Row
                    label='Tổng quãng đường'
                    value={km(totals.totalDistance)}
                    status={details.totalQD}
                  />
                  <Row
                    label='TG đúng hạng'
                    value={convertHoursToHM(totals.totalDurationDungHang)}
                    status={details.TG_dung_hang}
                  />
                  <Row
                    label='QĐ đúng hạng'
                    value={km(totals.totalDistanceDungHang)}
                  />
                  <Row
                    label='TG đêm đúng hạng'
                    value={convertHoursToHM(totals.totalNightDurationDungHang)}
                    status={details.TG_ban_dem_dung_hang}
                  />
                  <Row
                    label='TG tự động'
                    value={convertHoursToHM(totals.totalAutoDuration)}
                    status={details.TG_tu_dong}
                  />
                  <Row
                    label='QĐ tự động'
                    value={km(totals.totalAutoDistance)}
                    highlight={(totals.totalAutoDistance || 0) > 120}
                  />
                  {totals.manualNightDuration ? (
                    <Row
                      label='TG đêm nhập tay'
                      value={convertHoursToHM(totals.manualNightDuration)}
                    />
                  ) : null}
                </tbody>
              </Table>
            )}

            <div>
              Kết luận:{' '}
              {isPass ? (
                <Badge bg='success'>Đã đủ DAT</Badge>
              ) : (
                <Badge bg='danger'>
                  {summary.allChecked ? 'Chưa đủ điều kiện' : 'Chưa kiểm đủ phiên'}
                </Badge>
              )}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
